<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="480px"
    :lock-scroll="false"
    :append-to-body="true"
    :close-on-click-modal="true"
    @close="handleClose"
    custom-class="report-dialog"
  >
    <!--
      举报用户 / 群 / 评论时回显被举报对象：弹窗里全是原因标签和输入框，
      不点名的话用户在滚到底部时已经不确定自己在举报谁了。
      评论区一屏好几条，回显那条评论的正文尤其必要。
      举报帖子不需要——弹窗就是从那篇帖子上点开的，上下文很明确。
    -->
    <div class="report-target" v-if="showTargetName && targetName">
      <i :class="isCommentTarget ? 'el-icon-chat-line-square' : 'el-icon-user-solid'"></i>
      <span class="report-target-name">{{ targetName }}</span>
    </div>
    <el-form ref="reportForm" :model="form" :rules="rules" label-position="top" size="small">
      <el-form-item label="举报原因" prop="reportType">
        <div class="report-type-list" v-loading="typesLoading">
          <span
            class="report-type-tag"
            v-for="item in reportTypes"
            :key="item.code"
            :class="{ active: form.reportType === item.code }"
            @click="form.reportType = item.code"
          >{{ item.desc }}</span>
        </div>
      </el-form-item>
      <el-form-item label="补充说明（选填）">
        <el-input
          type="textarea"
          v-model="form.description"
          :rows="3"
          maxlength="500"
          show-word-limit
          placeholder="请补充说明举报原因"
          resize="none"
        />
      </el-form-item>
      <el-form-item label="举报材料（选填）">
        <div class="upload-area">
          <div class="upload-list">
            <div class="upload-item" v-for="(url, index) in imageList" :key="index">
              <!-- 已上传到 COS 的图，预览格子很小，用缩略图 -->
              <smart-img mode="content" :size="192" :src="url" alt="举报材料" />
              <div class="upload-item-remove" @click="removeImage(index)">
                <i class="el-icon-close"></i>
              </div>
            </div>
            <el-upload
              v-if="imageList.length < maxImageCount"
              class="upload-add"
              :show-file-list="false"
              action="#"
              :http-request="uploadImage"
              :before-upload="beforeImageUpload"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            >
              <div class="upload-add-btn">
                <i class="el-icon-plus"></i>
              </div>
            </el-upload>
          </div>
          <p class="upload-hint">最多{{ maxImageCount }}张，支持 JPG / PNG / GIF / WebP</p>
        </div>
      </el-form-item>
    </el-form>
    <span slot="footer">
      <el-button size="small" @click="dialogVisible = false">取消</el-button>
      <el-button size="small" type="primary" :loading="submitting" @click="handleSubmit">提交举报</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { articleFetch, userFetch } from '@/api'
import { reportChatGroup } from '@/api/group'
import { uploadImageToCos, compressImage } from '@/utils/cosUpload'

const MAX_IMAGE_COUNT = 3
const MAX_IMAGE_SIZE = 5

export default {
  name: 'ReportDialog',
  props: {
    visible: { type: Boolean, default: false },
    /**
     * 举报对象类型：'article' 举报帖子（默认）| 'user' 举报用户 | 'group' 举报群 | 'comment' 举报评论。
     * 交互完全一样（原因 + 说明 + 材料图），只有标题和提交的接口不同，
     * 所以在同一个组件里分流，而不是复制一份 300 行的弹窗。
     */
    targetType: {
      type: String,
      default: 'article',
      validator: (v) => ['article', 'user', 'group', 'comment'].includes(v)
    },
    /**
     * 被举报的帖子 id。
     * 原来是 required: true，加了「举报用户」之后不能再强制——
     * 举报用户时本来就没有 articleId，留着 required 会在控制台刷一堆 prop 警告。
     * 改成在提交时按 targetType 校验，缺 id 直接拦住并提示。
     */
    articleId: { type: [Number, String], default: '' },
    /** 被举报的用户 id，targetType === 'user' 时必传 */
    reportedUserId: { type: [Number, String], default: '' },
    /** 被举报的群 id，targetType === 'group' 时必传 */
    groupId: { type: [Number, String], default: '' },
    /** 被举报的评论 id，targetType === 'comment' 时必传（雪花 id，按字符串传） */
    commentId: { type: [Number, String], default: '' },
    /** 被举报对象的名称，举报用户/群时回显名字，举报评论时回显评论摘要 */
    targetName: { type: String, default: '' }
  },
  data () {
    return {
      maxImageCount: MAX_IMAGE_COUNT,
      imageList: [],       // 预览URL列表
      pendingFiles: {},    // previewUrl -> File 映射
      reportTypes: [],
      typesLoading: false,
      submitting: false,
      form: { reportType: null, description: '' },
      rules: {
        reportType: [{ required: true, message: '请选择举报原因', trigger: 'change' }]
      }
    }
  },
  computed: {
    dialogVisible: {
      get () { return this.visible },
      set (val) { this.$emit('update:visible', val) }
    },
    isUserTarget () {
      return this.targetType === 'user'
    },
    isGroupTarget () {
      return this.targetType === 'group'
    },
    isCommentTarget () {
      return this.targetType === 'comment'
    },
    showTargetName () {
      return this.isUserTarget || this.isGroupTarget || this.isCommentTarget
    },
    dialogTitle () {
      if (this.isUserTarget) return '举报用户'
      if (this.isGroupTarget) return '举报群聊'
      if (this.isCommentTarget) return '举报评论'
      return '举报内容'
    }
  },
  watch: {
    visible (val) {
      if (val && !this.reportTypes.length) this.fetchReportTypes()
    },
    /**
     * 切换举报对象类型时作废已缓存的原因列表。
     *
     * 帖子和用户目前返回的是同一套 ReportTypeEnum，但走的是两个接口，
     * 只要有一方以后调整了类型，共用一份缓存就会拿到另一方的列表
     * （uniapp 端的 loadReportTypes 也是按这个思路按目标区分缓存的）。
     */
    targetType () {
      this.reportTypes = []
    }
  },
  methods: {
    async fetchReportTypes () {
      this.typesLoading = true
      try {
        let res
        if (this.isUserTarget || this.isGroupTarget) {
          res = await userFetch.getUserReportTypes()
        } else if (this.isCommentTarget) {
          res = await userFetch.getCommentReportTypes()
        } else {
          res = await articleFetch.getReportTypes()
        }
        this.reportTypes = res.data || []
      } catch (e) {
        console.error('获取举报类型失败:', e)
      } finally {
        this.typesLoading = false
      }
    },
    removeImage (index) {
      const url = this.imageList[index]
      if (this.pendingFiles[url]) {
        URL.revokeObjectURL(url)
        this.$delete(this.pendingFiles, url)
      }
      this.imageList.splice(index, 1)
    },
    beforeImageUpload (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      if (!validTypes.includes(file.type.toLowerCase())) {
        this.$toast.error('只支持 JPG、PNG、GIF、WebP 格式的图片!')
        return false
      }
      if (file.size / 1024 / 1024 > MAX_IMAGE_SIZE) {
        this.$toast.error(`图片大小不能超过${MAX_IMAGE_SIZE}MB!`)
        return false
      }
      return true
    },
    async uploadImage (param) {
      try {
        const processedFile = await compressImage(param.file)
        const previewUrl = URL.createObjectURL(processedFile)
        this.imageList.push(previewUrl)
        this.$set(this.pendingFiles, previewUrl, processedFile)
        param.onSuccess({ data: previewUrl })
      } catch (error) {
        console.error('图片处理失败:', error)
        param.onError(error)
        this.$toast.error(error.message || '图片处理失败，请重试')
      }
    },
    async handleSubmit () {
      try { await this.$refs.reportForm.validate() } catch { return }

      // 目标 id 的校验从 prop 的 required 挪到这里：举报用户没有 articleId、
      // 举报帖子没有 reportedUserId，靠 prop 层面没法只对其中一个强制
      let targetId = this.articleId
      if (this.isUserTarget) targetId = this.reportedUserId
      else if (this.isGroupTarget) targetId = this.groupId
      else if (this.isCommentTarget) targetId = this.commentId
      if (!targetId) {
        this.$toast.error('举报对象信息缺失，请刷新后重试')
        return
      }

      this.submitting = true
      try {
        // 提交时批量上传图片到COS
        const uploadedImages = []
        for (const previewUrl of this.imageList) {
          if (this.pendingFiles[previewUrl]) {
            const cosUrl = await uploadImageToCos(this.pendingFiles[previewUrl], {
              directory: 'report',
              maxSize: MAX_IMAGE_SIZE,
              compress: false
            })
            uploadedImages.push(cosUrl)
            URL.revokeObjectURL(previewUrl)
          } else {
            uploadedImages.push(previewUrl)
          }
        }

        const data = {
          reportType: this.form.reportType,
          description: this.form.description || undefined,
          images: uploadedImages.length ? uploadedImages : undefined
        }
        if (this.isUserTarget) {
          data.reportedUserId = this.reportedUserId
          await userFetch.reportUser(data)
        } else if (this.isGroupTarget) {
          await reportChatGroup(this.groupId, data)
        } else if (this.isCommentTarget) {
          data.commentId = String(this.commentId)
          await userFetch.reportComment(data)
        } else {
          data.articleId = this.articleId
          await articleFetch.reportArticle(data)
        }
        this.$toast.success('举报成功')
        this.pendingFiles = {}
        this.dialogVisible = false
      } catch (error) {
        this.$toast.error(error.message || '举报失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    },
    handleClose () {
      this.form = { reportType: null, description: '' }
      // 释放所有本地预览URL
      for (const url of this.imageList) {
        if (this.pendingFiles[url]) {
          URL.revokeObjectURL(url)
        }
      }
      this.imageList = []
      this.pendingFiles = {}
      this.$refs.reportForm && this.$refs.reportForm.resetFields()
    }
  }
}
</script>

<style lang="scss" scoped>
/* 被举报对象回显（仅举报用户时出现） */
.report-target {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  margin-bottom: 16px;
  background: #f5f7fa;
  border-radius: 6px;

  i {
    font-size: 14px;
    color: #909399;
    margin-right: 6px;
    flex-shrink: 0;
  }

  .report-target-name {
    font-size: 13px;
    color: #303133;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.report-type-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 32px;

  .report-type-tag {
    display: inline-block;
    padding: 5px 14px;
    border: 1px solid #e4e7ed;
    border-radius: 16px;
    font-size: 13px;
    color: #606266;
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
    background: #fff;
    line-height: 1.4;

    &:hover {
      border-color: #409eff;
      color: #409eff;
      background: #ecf5ff;
    }

    &.active {
      background: #409eff;
      border-color: #409eff;
      color: #fff;
    }
  }
}

.upload-area {
  .upload-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .upload-item {
    position: relative;
    width: 72px;
    height: 72px;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #ebeef5;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .upload-item-remove {
      position: absolute;
      top: 2px;
      right: 2px;
      width: 18px;
      height: 18px;
      background: rgba(0, 0, 0, 0.55);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.2s;

      &:hover { background: rgba(0, 0, 0, 0.8); }

      i { color: #fff; font-size: 10px; }
    }
  }

  .upload-add {
    .upload-add-btn {
      width: 72px;
      height: 72px;
      border: 1px dashed #dcdfe6;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      background: #fafafa;
      transition: all 0.2s;

      &:hover {
        border-color: #409eff;
        background: #ecf5ff;
      }

      i { font-size: 20px; color: #c0c4cc; }
      &:hover i { color: #409eff; }
    }
  }

  .upload-hint {
    color: #c0c4cc;
    font-size: 12px;
    margin-top: 6px;
    line-height: 1;
  }
}
</style>

<style>
.report-dialog {
  border-radius: 12px;
  overflow: hidden;
}
.report-dialog .el-dialog__header {
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
}
.report-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.report-dialog .el-dialog__body {
  padding: 16px 20px;
  max-height: 60vh;
  overflow-y: auto;
}
.report-dialog .el-dialog__footer {
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
}
.report-dialog .el-form-item {
  margin-bottom: 16px;
}
.report-dialog .el-form-item__label {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  padding-bottom: 6px !important;
  line-height: 1.4 !important;
}
</style>
